import { LoadingBoundary } from '@app/pages/i/components/LoadingBoundary';
import { BubbleItem } from '@app/types/shop-bff/types';
import {
  Box,
  ChevronLeftBigIcon,
  ChevronRightBigIcon,
  constants,
  Text,
} from '@redbubble/design-system';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import {
  AnalyticsContextTypes,
  AnalyticsStorage,
  AnalyticsStorageKeys,
} from '@redbubble/next-analytics/gtm';
import { kebabCase } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { TopicLoading } from './loading';
import styles from './RelatedTopicsList.module.scss';
import { Topic } from './Topic';

export const messages = defineMessages({
  relatedTopics: {
    defaultMessage: 'Related Topics:',
  },
});

interface RelatedTopicsListProps {
  topics: BubbleItem[];
  event?: (urlPath: string) => void;
  seoVisible?: boolean;
}

const handleClick =
  (
    topic: BubbleItem,
    event: RelatedTopicsListProps['event'],
    position: number,
    seoVisible?: boolean
  ) =>
  (clickEvent?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (seoVisible && clickEvent) clickEvent.preventDefault();
    AnalyticsStorage.setValue(
      AnalyticsStorageKeys.RefContextType,
      AnalyticsContextTypes.TopicPill
    );
    AnalyticsStorage.setValue(
      AnalyticsStorageKeys.RefContextIndex,
      `${position}`
    );
    AnalyticsStorage.setValue(
      AnalyticsStorageKeys.RefContextLabel,
      topic.title
    );

    if (event) event(topic.url);
    window.location.assign(topic.url);
  };

export const RelatedTopicsList: React.FC<RelatedTopicsListProps> = ({
  topics,
  event,
  seoVisible,
}) => {
  const intl = useIntl();

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollNext, setScrollNext] = useState(false);
  const [scrollPrev, setScrollPrev] = useState(false);

  const handleScroll = (direction: string) => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = container.clientWidth * 0.8;
      if (direction === 'next') {
        container.scrollLeft += scrollAmount;
      } else {
        container.scrollLeft -= scrollAmount;
      }
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const updateScrollPosition = () => {
      const scrollLeft = container.scrollLeft;
      const maxScroll = container.scrollWidth - container.clientWidth;

      setScrollNext(scrollLeft < maxScroll);
      setScrollPrev(scrollLeft > 0);
    };

    container.addEventListener('scroll', updateScrollPosition);
    updateScrollPosition();

    return () => container.removeEventListener('scroll', updateScrollPosition);
  }, []);

  return (
    <Box
      className={styles.carouselWrapper}
      data-testid="related-topics-carousel"
    >
      <Box className={styles.relatedTopicsMobile}>
        <Text type="display5">
          {intl.formatMessage(messages.relatedTopics)}
        </Text>
      </Box>
      <Box className={styles.carouselContainer} ref={scrollContainerRef}>
        <Box className={styles.carouselInner}>
          <Text type="display5" className={styles.relatedTopicsDesktop}>
            {intl.formatMessage(messages.relatedTopics)}
          </Text>
          {topics.map((topic, index) => {
            return (
              <Box data-testid={`${kebabCase(topic.title)}-topic`}>
                <LoadingBoundary loadingComponent={<TopicLoading />}>
                  <Topic
                    title={topic.title}
                    href={seoVisible ? topic.url : undefined}
                    element="a"
                    onClick={handleClick(topic, event, index, seoVisible)}
                  />
                </LoadingBoundary>
              </Box>
            );
          })}
        </Box>
      </Box>
      {scrollPrev && (
        <button
          type="button"
          className={styles.prevButton}
          onClick={() => handleScroll('prev')}
          data-testid="rp-carousel-prev-button"
        >
          <ChevronLeftBigIcon size={constants.MEDIUM} />
        </button>
      )}
      {scrollNext && (
        <button
          type="button"
          className={styles.nextButton}
          onClick={() => handleScroll('next')}
          data-testid="rp-carousel-next-button"
        >
          <ChevronRightBigIcon size={constants.MEDIUM} />
        </button>
      )}
    </Box>
  );
};
